import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  EnvelopeOpen,
  Sparkle,
  ChartLineUp,
  House,
  WarningOctagon,
  ArrowRight,
} from "@phosphor-icons/react";

const features = [
  {
    icon: House,
    title: "Lease renewals, drafted for you",
    desc: "One click to generate a warm, professional renewal email tailored to each tenant and property.",
  },
  {
    icon: WarningOctagon,
    title: "Past-due, prioritized",
    desc: "See outstanding balances ranked by severity and send compassionate AI-drafted notices in seconds.",
  },
  {
    icon: EnvelopeOpen,
    title: "Inbox triage",
    desc: "AI auto-categorizes Outlook emails into emergency, past-due, vendor, coworker, and renewal — so nothing slips.",
  },
  {
    icon: ChartLineUp,
    title: "Market trend research",
    desc: "Zip code + price range → trending keywords, demographics, and demand signals for smarter pricing.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-sand-100">
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <Logo />
        <nav className="flex items-center gap-3">
          <Link to="/login" className="text-sm text-stone-600 hover:text-forest-600" data-testid="nav-login">
            Sign in
          </Link>
          <Link to="/signup" data-testid="nav-signup">
            <Button className="bg-forest-600 hover:bg-forest-500 text-white">Get started</Button>
          </Link>
        </nav>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-8 pb-20 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-forest-50 text-forest-600 text-xs uppercase tracking-[0.18em] mb-6" data-testid="landing-eyebrow">
            <Sparkle size={14} weight="fill" /> AI for property managers
          </div>
          <h1 className="font-heading text-5xl lg:text-6xl font-semibold tracking-tight text-stone-900 leading-[1.05]">
            Renewals, collections, and inbox chaos — <span className="brand-gradient-text">solved.</span>
          </h1>
          <p className="mt-6 text-lg text-stone-600 max-w-xl leading-relaxed">
            rentmore24 is the AI leasing agent that drafts renewal emails, chases past-due balances,
            sorts your Outlook inbox by urgency, and researches rental market trends by zip code — all in one quiet, beautiful workspace.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/signup" data-testid="landing-cta-primary">
              <Button size="lg" className="bg-forest-600 hover:bg-forest-500 text-white h-12 px-6">
                Start free — demo data included <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <Link to="/login" data-testid="landing-cta-secondary">
              <Button size="lg" variant="outline" className="h-12 px-6 border-stone-300">
                Sign in
              </Button>
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
            <Stat label="Renewals / mo" value="240+" />
            <Stat label="Inbox sorted" value="98%" />
            <Stat label="Time saved" value="11 hrs/wk" />
          </div>
        </div>

        <div className="lg:col-span-5">
          <div
            className="relative rounded-2xl overflow-hidden h-[520px] shadow-[0_30px_60px_-30px_rgba(27,67,50,0.35)]"
            style={{
              backgroundImage:
                "linear-gradient(160deg, rgba(27,67,50,0.0), rgba(27,67,50,0.35)), url(https://images.unsplash.com/photo-1776090974926-0ffca9a8177e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBleHRlcmlvciUyMGRheWxpZ2h0fGVufDB8fHx8MTc3Njk3OTY5M3ww&ixlib=rb-4.1.0&q=85)",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-5 border border-stone-200">
              <div className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-1">AI Draft</div>
              <div className="font-heading font-semibold text-stone-900">Renewal — 142 Oakwood Drive</div>
              <p className="text-sm text-stone-600 mt-2 leading-relaxed line-clamp-3">
                Hi Marcus — as your lease approaches its end on March 18th, we'd love to invite you to renew
                for another 12 months at $2,320/month (a modest $120 adjustment). You've been a wonderful part of the Oakwood community…
              </p>
              <div className="mt-3 flex gap-2">
                <span className="text-[10px] uppercase tracking-[0.2em] bg-forest-50 text-forest-600 px-2 py-1 rounded">Renewal</span>
                <span className="text-[10px] uppercase tracking-[0.2em] bg-sand-200 text-stone-500 px-2 py-1 rounded">Ready to send</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-2 gap-5">
          {features.map((f) => (
            <div key={f.title} className="bg-white border border-stone-200 rounded-xl p-6 hover-lift" data-testid={`feature-${f.title}`}>
              <div className="w-10 h-10 rounded-lg bg-forest-50 text-forest-600 flex items-center justify-center mb-4">
                <f.icon size={22} weight="duotone" />
              </div>
              <h3 className="font-heading text-xl font-medium text-stone-900">{f.title}</h3>
              <p className="mt-2 text-sm text-stone-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-stone-200">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between text-sm text-stone-500">
          <div>© 2026 rentmore24.com</div>
          <div>Built with care for property teams.</div>
        </div>
      </footer>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="font-heading text-2xl font-semibold text-stone-900">{value}</div>
    <div className="text-xs uppercase tracking-[0.18em] text-stone-400 mt-1">{label}</div>
  </div>
);
